import { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import './LocationPicker.css';

const DEFAULT_CENTER = [35.8, -86.4]; // Tennessee
const DEFAULT_ZOOM = 7;
const MARKER_STYLE = {
  radius: 11,
  fillColor: '#bc9038',
  fillOpacity: 0.95,
  color: '#fff',
  weight: 3,
};

/**
 * Interactive Leaflet map for picking a lat/lon location.
 * Click to place a marker, drag to reposition.
 * Two-way synced with external lat/lon form fields.
 * Supports fullscreen expand mode.
 */
const LocationPicker = ({ latitude, longitude, onChange }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const hintRef = useRef(null);
  const readoutRef = useRef(null);
  // Track the last values we set, so external-only changes trigger a pan
  const lastSetRef = useRef({ lat: null, lng: null });

  const [expanded, setExpanded] = useState(false);

  const hasCoords = latitude !== '' && longitude !== '' &&
    latitude != null && longitude != null &&
    !isNaN(parseFloat(latitude)) && !isNaN(parseFloat(longitude));

  // Round to 6 decimal places (model precision)
  const round6 = (n) => Math.round(n * 1e6) / 1e6;

  // Place or move marker, update readout, fire onChange
  const placeMarker = useCallback((lat, lng, fireChange = true) => {
    const L = window.L;
    if (!L || !mapRef.current) return;

    const rounded = { lat: round6(lat), lng: round6(lng) };

    if (markerRef.current) {
      markerRef.current.setLatLng([rounded.lat, rounded.lng]);
    } else {
      const marker = L.circleMarker([rounded.lat, rounded.lng], MARKER_STYLE)
        .addTo(mapRef.current);
      markerRef.current = marker;
    }

    // Hide hint
    if (hintRef.current) hintRef.current.style.display = 'none';

    // Update readout
    if (readoutRef.current) {
      readoutRef.current.textContent =
        `${rounded.lat.toFixed(6)}, ${rounded.lng.toFixed(6)}`;
    }

    lastSetRef.current = { lat: String(rounded.lat), lng: String(rounded.lng) };

    if (fireChange) {
      onChange(String(rounded.lat), String(rounded.lng));
    }
  }, [onChange]);

  // Remove marker and show hint
  const clearMarker = useCallback(() => {
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
    if (hintRef.current) hintRef.current.style.display = '';
    if (readoutRef.current) readoutRef.current.textContent = '';
    lastSetRef.current = { lat: null, lng: null };
  }, []);

  // Toggle fullscreen
  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  // Escape key closes fullscreen
  useEffect(() => {
    if (!expanded) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [expanded]);

  // Lock body scroll when expanded
  useEffect(() => {
    if (expanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [expanded]);

  // Invalidate map size when expand/collapse changes the container dimensions
  useEffect(() => {
    if (!mapRef.current) return;
    // Small delay so CSS transition / layout completes
    const timer = setTimeout(() => {
      mapRef.current.invalidateSize();
      // Also enable/disable scroll zoom based on mode
      if (expanded) {
        mapRef.current.scrollWheelZoom.enable();
      } else {
        mapRef.current.scrollWheelZoom.disable();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [expanded]);

  // Initialize map
  useEffect(() => {
    const L = window.L;
    if (!L || !containerRef.current) return;

    // Prevent double-init
    if (mapRef.current) return;

    const map = L.map(containerRef.current, {
      scrollWheelZoom: false,
      zoomControl: true,
    });
    mapRef.current = map;

    // Esri hybrid: satellite imagery + road/place label overlay
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics',
      maxZoom: 19,
    }).addTo(map);
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 19,
      pane: 'overlayPane',
    }).addTo(map);

    // Set initial view
    if (hasCoords) {
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);
      map.setView([lat, lng], 10);
      placeMarker(lat, lng, false);
    } else {
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    }

    // Click handler
    map.on('click', (e) => {
      placeMarker(e.latlng.lat, e.latlng.lng);
    });

    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync marker when external lat/lon values change (e.g., typing in inputs)
  useEffect(() => {
    if (!mapRef.current) return;

    // If both are cleared, remove marker
    if (!hasCoords) {
      if (markerRef.current) clearMarker();
      return;
    }

    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (isNaN(lat) || isNaN(lng)) return;

    // Skip if we set these values ourselves
    const last = lastSetRef.current;
    if (String(round6(lat)) === last.lat && String(round6(lng)) === last.lng) return;

    placeMarker(lat, lng, false);
    mapRef.current.setView([lat, lng], Math.max(mapRef.current.getZoom(), 10));
  }, [latitude, longitude, hasCoords, placeMarker, clearMarker]);

  // Fallback if Leaflet CDN failed
  if (!window.L) {
    return (
      <div className="location-picker location-picker--fallback">
        <p className="location-picker__fallback-text">Map unavailable</p>
      </div>
    );
  }

  return (
    <div className={`location-picker${expanded ? ' location-picker--expanded' : ''}`}>
      {expanded && <div className="location-picker__backdrop" onClick={toggleExpanded} />}
      <div className="location-picker__container">
        <div ref={containerRef} className="location-picker__map" />
        <span
          ref={hintRef}
          className="location-picker__hint"
          style={hasCoords ? { display: 'none' } : undefined}
        >
          Click the map to set location
        </span>
        <span ref={readoutRef} className="location-picker__readout">
          {hasCoords
            ? `${parseFloat(latitude).toFixed(6)}, ${parseFloat(longitude).toFixed(6)}`
            : ''}
        </span>
        <button
          type="button"
          className="location-picker__expand-btn"
          onClick={toggleExpanded}
          title={expanded ? 'Exit fullscreen (Esc)' : 'Expand map'}
          aria-label={expanded ? 'Exit fullscreen' : 'Expand map to fullscreen'}
        >
          {expanded ? (
            /* Collapse / minimize icon */
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 2v3a1 1 0 0 1-1 1H2M10 2v3a1 1 0 0 0 1 1h3M6 14v-3a1 1 0 0 0-1-1H2M10 14v-3a1 1 0 0 1 1-1h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            /* Expand / fullscreen icon */
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 6V3a1 1 0 0 1 1-1h3M14 6V3a1 1 0 0 0-1-1h-3M2 10v3a1 1 0 0 0 1 1h3M14 10v3a1 1 0 0 1-1 1h-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </button>
        {expanded && (
          <div className="location-picker__esc-hint">
            Press <kbd>Esc</kbd> to close
          </div>
        )}
      </div>
    </div>
  );
};

LocationPicker.propTypes = {
  latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
};

export default LocationPicker;
